class ProductCardList extends window.BAO.CustomElement() {
  // Batch multiple Klevu API requests to stay under 200 req/min rate limit.
  // Use static properties so that all <product-card-list> share the same
  // queue and batch timer.
  static requestQueue = []
  static batchTimer = null
  static BATCH_SIZE = 16
  static DEBOUNCE_MS = 50

  static get requiredElements () {
    return ['item']
  }

  connectedCallback () {
    super.connectedCallback()

    this.market = `${window.Shopify.currency.active}_${window.Shopify.country}`

    this.Id = this.dataset.id
    this.collectionTitle = this.dataset.collectionTitle
    this.section = this.dataset.section || 'product-card'
    this.limit = this.dataset.limit || 8

    this.fetchProductRecommendations()
  }

  fetchProductRecommendations () {
    return new Promise((resolve, reject) => {
      const requestId = `productCardList-${this.Id}-${Date.now()}`

      // Queue this instance's request with other <product-card-list> elements on the page.
      ProductCardList.requestQueue.push({
        id: requestId,
        instance: this,
        resolve,
        reject,
        query: {
          id: requestId,
          typeOfRequest: 'CATNAV',
          settings: {
            limit: this.limit,
            sort: 'RELEVANCE',
            query: {
              categoryPath: this.collectionTitle,
            },
            fields: ['url'],
            searchPrefs: ['showOutOfStockProducts'],
          },
        },
      })

      // Reset timer to collect all instances loading together
      clearTimeout(ProductCardList.batchTimer)
      ProductCardList.batchTimer = setTimeout(() => {
        ProductCardList.processBatch()
      }, ProductCardList.DEBOUNCE_MS)
    })
  }

  static async processBatch () {
    if (ProductCardList.requestQueue.length === 0) return

    const apiKey = window.theme.collection.klevuAPIKey
    const endpoint = window.theme.collection.klevuAPIURL
    const url = `https://${endpoint}/cs/v2/search`

    // Take up to 8 requests from queue
    const currentBatch = ProductCardList.requestQueue.splice(
      0,
      ProductCardList.BATCH_SIZE,
    )

    // Send all queries in single API call
    const body = {
      recordQueries: currentBatch.map(req => req.query),
      context: {
        apiKeys: [apiKey],
      },
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const data = await response.json()

      // Distribute results back to each instance by index
      currentBatch.forEach((request, index) => {
        const products = data.queryResults?.[index]?.records || []
        request.instance.products = products
        request.instance.observeCards()
        request.resolve()
      })
    } catch (error) {
      currentBatch.forEach(request => {
        request.reject(error)
      })
    }

    // Process next batch if more requests remain
    if (ProductCardList.requestQueue.length > 0) {
      setTimeout(() => ProductCardList.processBatch(), 0)
    }
  }

  observeCards () {
    const items = this.els?.item?.elements || []

    // Priority elements render all cards immediately
    if (this.dataset.priority === 'true') {
      items.forEach((item, index) => {
        this.renderCard(item, index)
      })
      return
    }

    // Non-priority elements use IntersectionObserver
    this._observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const item = entry.target
            const index = items.indexOf(item)

            if (item.dataset.rendered === 'true') return

            this.renderCard(item, index)
            this._observer.unobserve(item)
          }
        })
      },
      {
        rootMargin: '400px',
        threshold: 0.01,
      },
    )

    items.forEach(item => {
      this._observer.observe(item)
    })
  }

  async renderCard (item, index) {
    const product = this.products[index]

    if (product) {
      item.dataset.rendered = 'true'

      switch (this.section) {
        case 'product-card':
          item.innerHTML = await this.renderDefaultProductCard(product)
          break
        case 'product-card-brand':
          item.innerHTML = await this.renderMakerProductCard(product)
          break
        default:
          item.innerHTML = await this.renderDefaultProductCard(product)
      }
    } else {
      item.remove()
    }
  }

  async renderDefaultProductCard (product) {
    const productUrl = product.url.replace(
      '/product',
      `${Shopify.routes.root}product`,
    )

    try {
      const html = await window.BAO.sectionRenderer.fetchSingle(
        'product-card',
        productUrl,
      )

      return html
    } catch (e) {
      console.error('Error fetching section:', e)

      return ''
    }
  }

  async renderMakerProductCard (product) {
    const productUrl = product.url.replace(
      '/product',
      `${Shopify.routes.root}product`,
    )

    try {
      const html = await window.BAO.sectionRenderer.fetchSingle(
        'product-card-brand',
        productUrl,
      )

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const linkEl = doc.querySelector('a')
      const linkTitleEl = doc.querySelector('a span')

      if (linkTitleEl && this.dataset.collectionTitle) {
        linkTitleEl.innerHTML = this.dataset.collectionTitle
      }
      if (linkEl && this.dataset.collectionHandle) {
        linkEl.href = `${Shopify.routes.root}collections/${this.dataset.collectionHandle}`
      }

      return doc.body.innerHTML
    } catch (e) {
      console.error('Error fetching section:', e)

      return ''
    }
  }
}

if (!customElements.get('product-card-list')) {
  customElements.define('product-card-list', ProductCardList)
}
